import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SkillVariable } from '../types';

interface SkillVariableEditorProps {
  variables: SkillVariable[];
  onChange: (variables: SkillVariable[]) => void;
  /** Template string to auto-detect {{xxx}} placeholders */
  systemPrompt: string;
}

/** Extract {{xxx}} placeholders from template string */
function extractPlaceholders(template: string): string[] {
  const regex = /\{\{(\w+)\}\}/g;
  const keys = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(template)) !== null) {
    keys.add(match[1]);
  }
  return Array.from(keys);
}

/**
 * SkillVariableEditor - used in Skill editing pages.
 * Left side: the user edits SystemPrompt with {{xxx}} placeholders.
 * Right side: this component auto-detects placeholders and lets the creator
 * declare metadata (label, type, required, default, options) for each variable.
 */
export default function SkillVariableEditor({ variables, onChange, systemPrompt }: SkillVariableEditorProps) {
  const { t } = useTranslation('skills');
  const detectedKeys = extractPlaceholders(systemPrompt);

  // Keys in template but not yet declared
  const undeclaredKeys = detectedKeys.filter(k => !variables.some(v => v.key === k));
  // Keys declared but no longer in template
  const orphanedKeys = variables.filter(v => !detectedKeys.includes(v.key)).map(v => v.key);

  const updateVariable = useCallback((key: string, patch: Partial<SkillVariable>) => {
    onChange(variables.map(v => v.key === key ? { ...v, ...patch } : v));
  }, [variables, onChange]);

  const addVariable = useCallback((key: string) => {
    onChange([...variables, {
      key,
      label: key,
      type: 'text',
      required: false,
    }]);
  }, [variables, onChange]);

  const removeVariable = useCallback((key: string) => {
    onChange(variables.filter(v => v.key !== key));
  }, [variables, onChange]);

  if (detectedKeys.length === 0 && variables.length === 0) {
    return (
      <div style={{ color: '#999', fontSize: 13, padding: '12px 0' }}>
        {t('variableEditor.emptyHint', { syntax: '{{变量名}}', example: '{{protagonist}}' })}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
        {t('variableEditor.detectedCount', { count: detectedKeys.length })}
      </div>

      {/* Auto-add buttons for undeclared keys */}
      {undeclaredKeys.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {undeclaredKeys.map(key => (
            <button
              key={key}
              onClick={() => addVariable(key)}
              style={{
                padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                border: '1px dashed #1890ff', borderRadius: 4, background: '#f0f7ff', color: '#1890ff',
              }}
            >
              + {key}
            </button>
          ))}
        </div>
      )}

      {/* Variable cards */}
      {variables.map(v => {
        const isOrphaned = orphanedKeys.includes(v.key);
        return (
          <div
            key={v.key}
            style={{
              border: `1px solid ${isOrphaned ? '#ffccc7' : '#d9d9d9'}`,
              borderRadius: 6, padding: 12, marginBottom: 10,
              background: isOrphaned ? '#fff2f0' : '#fafafa',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <code style={{ fontSize: 13, fontWeight: 600 }}>{`{{${v.key}}}`}</code>
              <button
                onClick={() => removeVariable(v.key)}
                style={{ fontSize: 12, color: '#ff4d4f', cursor: 'pointer', border: 'none', background: 'none' }}
              >
                {isOrphaned ? t('variableEditor.removeOrphaned') : t('variableEditor.remove')}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{ fontSize: 12 }}>{t('variableEditor.displayName')}
                <input
                  value={v.label}
                  onChange={e => updateVariable(v.key, { label: e.target.value })}
                  style={inputStyle}
                  placeholder={v.key}
                />
              </label>
              <label style={{ fontSize: 12 }}>{t('variableEditor.inputType')}
                <select
                  value={v.type}
                  onChange={e => updateVariable(v.key, { type: e.target.value as SkillVariable['type'] })}
                  style={inputStyle}
                >
                  <option value="text">{t('variableEditor.typeText')}</option>
                  <option value="textarea">{t('variableEditor.typeTextarea')}</option>
                  <option value="select">{t('variableEditor.typeSelect')}</option>
                </select>
              </label>
              <label style={{ fontSize: 12 }}>{t('variableEditor.defaultValue')}
                <input
                  value={v.default || ''}
                  onChange={e => updateVariable(v.key, { default: e.target.value })}
                  style={inputStyle}
                  placeholder={t('variableEditor.optional')}
                />
              </label>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                <input
                  type="checkbox"
                  checked={v.required}
                  onChange={e => updateVariable(v.key, { required: e.target.checked })}
                />{t('variableEditor.required')}
              </label>
            </div>

            <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>{t('variableEditor.helpText')}
              <input
                value={v.description || ''}
                onChange={e => updateVariable(v.key, { description: e.target.value })}
                style={inputStyle}
                placeholder={t('variableEditor.helpPlaceholder')}
              />
            </label>

            {v.type === 'select' && (
              <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                {t('variableEditor.optionsLabel')}
                <input
                  value={(v.options || []).join(', ')}
                  onChange={e => updateVariable(v.key, {
                    options: e.target.value.split(/[,，]/).map(s => s.trim()).filter(Boolean),
                  })}
                  style={inputStyle}
                  placeholder={t('variableEditor.optionsPlaceholder')}
                />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '4px 8px', fontSize: 13,
  border: '1px solid #d9d9d9', borderRadius: 4, marginTop: 2,
  boxSizing: 'border-box',
};
